import 'browser-report';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { MarkdownRender } from '@gov-nx/component/web';
import type { WizardFormStep } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { FormProblemData } from '@gov-nx/module/page';
import { usePodporaContext } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	FormFileUpload,
	FormInput,
	FormRadio,
	FormTextarea,
	FormWidthElementLayout,
	LayoutGap,
} from '@gov-nx/ui/web';
import { config, useCaptcha } from '@gov-nx/web';

interface WindowWithReport extends Window {
	browserReportSync: () => object;
}

declare const window: WindowWithReport;

interface PodporaProblemFormProps {
	formMethods: WizardFormStep<FormProblemData>['formDefinition'];
}

export const SupportProblemForm = ({
	formMethods,
}: PodporaProblemFormProps) => {
	const { isLogged, code, controls, fileUploadHook, zpusobPrihlaseniOptions } =
		usePodporaContext();
	const { captchaToken } = useCaptcha({ when: !isLogged });
	const { t } = useLocale(code);

	useEffect(() => {
		const reportResult = window.browserReportSync();
		const version = config.getCurrentVersion();
		const build = config.getCurrentBuild();
		formMethods.formMethods.setValue('technickeInformace', reportResult);
		formMethods.formMethods.setValue('verze', version);
		formMethods.formMethods.setValue('build', build);
		formMethods.formMethods.setValue('captcha', captchaToken);
	}, [captchaToken, formMethods.formMethods]);

	return (
		<FormProvider {...formMethods.formMethods}>
			<LayoutGap gap="2xl">
				<LayoutGap
					gap="xl"
					className="mt-4">
					<FormInput
						field={{
							name: 'souhrn',
							isDisabled: controls.processLoading,
							placeholder: t('formular.placeholders.nazev-problemu'),
						}}
						label={{ children: t('formular.pole.nazev-problemu') }}
					/>
					<FormTextarea
						field={{
							name: 'podrobnyPopis',
							isDisabled: controls.processLoading,
							placeholder: t('formular.placeholders.popis-problemu'),
							rows: 4,
						}}
						label={{ children: t('formular.pole.popis-problemu') }}
					/>
				</LayoutGap>
				<LayoutGap gap="xl">
					<FormRadio
						field={{
							size: 's',
							name: 'vyskytChyby',
							isDisabled: controls.processLoading,
							options: [
								{
									value: 'OPAKOVANE',
									label: { children: t('formular.pole.opakovane') },
								},
								{
									value: 'OJEDINELE',
									label: { children: t('formular.pole.ojedinele') },
								},
							],
						}}
						label={{
							children: t('formular.pole.chyba-se-vyskytuje'),
						}}
					/>
					<FormRadio
						field={{
							size: 's',
							name: 'drivejsiStav',
							isDisabled: controls.processLoading,
							options: [
								{
									value: 'SPUSTENO_POPRVE',
									label: { children: t('formular.pole.spoustim-poprve') },
								},
								{
									value: 'DRIVE_FUNGOVALO',
									label: { children: t('formular.pole.drive-fungovala') },
								},
							],
						}}
						label={{
							children: t('formular.pole.chyba-ve-funkci'),
						}}
					/>
					{!isLogged && (
						<FormRadio
							field={{
								size: 's',
								name: 'zpusobPrihlaseni',
								isDisabled: controls.processLoading,
								options: zpusobPrihlaseniOptions,
							}}
							label={{
								children: t('formular.pole.zpusob-prihlaseni'),
							}}
						/>
					)}
				</LayoutGap>
				{formMethods.formMethods.watch('zpusobPrihlaseni') === 'JMENO_HESLO' ? (
					<FormWidthElementLayout>
						<LayoutGap gap="m">
							<FormInput
								field={{
									name: 'prihlaseniUserName',
									isDisabled: controls.processLoading,
									placeholder: t(
										'formular.placeholders.zpusob-prihlaseni-jmeno'
									),
								}}
								label={{
									children: t('formular.pole.zpusob-prihlaseni-jmeno'),
								}}
							/>
							<FormInput
								field={{
									name: 'prihlaseniEmail',
									isDisabled: controls.processLoading,
									placeholder: t(
										'formular.placeholders.zpusob-prihlaseni-email'
									),
								}}
								label={{
									children: t('formular.pole.zpusob-prihlaseni-email'),
								}}
							/>
							<FormInput
								field={{
									name: 'prihlaseniTelefon',
									isDisabled: controls.processLoading,
									placeholder: t(
										'formular.placeholders.zpusob-prihlaseni-telefon'
									),
								}}
								label={{
									children: t('formular.pole.zpusob-prihlaseni-telefon'),
								}}
							/>
						</LayoutGap>
					</FormWidthElementLayout>
				) : null}
				{formMethods.formMethods.watch('zpusobPrihlaseni') === 'DS' ? (
					<FormWidthElementLayout>
						<FormInput
							field={{
								name: 'prihlaseniIdDS',
								isDisabled: controls.processLoading,
								placeholder: t('formular.placeholders.zpusob-prihlaseni-ds'),
							}}
							label={{ children: t('formular.pole.zpusob-prihlaseni-ds') }}
						/>
					</FormWidthElementLayout>
				) : null}
				<LayoutGap gap="m">
					<h4 className="text-headline-m!">{t('formular.pole.priloha')}</h4>
					<p className="text-body-s!">{t('formular.pole.soubor-informace')}</p>
					<FormFileUpload
						fileUploadHook={fileUploadHook}
						field={{
							name: 'priloha',
							expanded: true,
							disabled: controls.processLoading,
						}}
						// label={{ children: t('formular.pole.file-upload') }}
						group={{ gap: 's' }}
					/>
					<p className="text-body-s!">
						{t('formular.zprava.nahrat-soubor-popis')}
					</p>
					<MarkdownRender applyClasses={{ paragraph: 'text-body-s!' }}>
						{t('formular.zprava.chraneno-recaptcha')}
					</MarkdownRender>
				</LayoutGap>
				<ButtonGroupLayout>
					<Button
						isLoading={controls.processLoading}
						aria-label={t('formular.wcag.pokracovat')}
						isDisabled={
							fileUploadHook.hasNotFinishedUploads || controls.processLoading
						}
						nativeType="submit"
						color="primary"
						size="l"
						type="solid">
						{t('formular.tlacitko.pokracovat')}
					</Button>
				</ButtonGroupLayout>
			</LayoutGap>
		</FormProvider>
	);
};
